/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import { Actions, Store } from '@ngxs/store';

import { DateTime } from 'luxon';

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiSuccessResponse } from '../../../types/Rest';
import { SetSelection } from '../../map/state/selection.actions';
import { LoggerService } from '../../shared/services/logger.service';

import { RestService } from '../../shared/services/rest.service';
import { DataFieldValueModel, DataFormValueModel, TableDataFormData } from '../state/data-form.model';
import { ReportService } from './report.service';

@Injectable()
export class DataFormService {
    /**
     * @param logger {LoggerService}
     * @param rest {RestService}
     * @param reportService {ReportService}
     * @param actions$ {Actions}
     * @param store {Store}
     */
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private reportService: ReportService,
        private actions$: Actions,
        private store: Store
    ) {
        this.reportService = reportService;
    }

    public saveData(dataFormValues: DataFormValueModel) {
        const postData = {
            name: dataFormValues.name,
            keys: dataFormValues.keys,
            rows: [
                {
                    keys: dataFormValues.keys,
                    fields: dataFormValues.fields
                        .filter(f => f.dirty || f.value !== null || f.value !== '')
                        .map(f => {
                            // convert Luxon DateTime to JS Date value
                            if (DateTime.isDateTime(f.value)) {
                                // objects from state are frozen
                                const newField: DataFieldValueModel = { ...f };
                                newField.value = (f.value as DateTime).toFormat('yyyy-MM-dd');
                                return newField;
                            }
                            return f;
                        }),
                }
            ],
        };
        return this.rest
            .apiPost<ApiSuccessResponse>(`dataform/save`, postData, {}, { skipErrorHandler: false })
            .pipe(tap(() => this.store.dispatch(new SetSelection())));
    }

    public saveTableData(name: string, tableData: TableDataFormData) {
        const keys = Object.keys(tableData);
        const postData = {
            name,
            keys: Object.keys(tableData),
            rows: keys.map((key) => {
                const fields = Object.keys(tableData[key]);
                return {
                    keys: [key],
                    fields: fields.map((fieldName) => {
                        const data = tableData[key];
                        return {
                            name: fieldName,
                            value: data[fieldName],
                        };
                    }),
                };
            }),
        };
        return this.rest.apiPost('dataform/save', postData);
    }

    public getDropDownOptions$(formName, fieldName, parameterValue): Observable<any> {
        const params = { value: parameterValue };
        return this.rest.apiGet(`dataform/options/${formName}/${fieldName}`, params);
    }
}
